import type { CrewSweepRow, FlowSweepRow } from "./ganttTypes";
import { CHART, fmtInt, niceTicks, stepDecimals } from "./chartUtils";
import type { UnitSystem } from "@/lib/units";
import { flowToDisp, flowUnit, fmtFlow, fmtMassT, goalLabel, tickSuffix, tToDisp } from "./ganttUnits";

/**
 * Draws the optimiser chart (throughput vs crews bar chart, or throughput vs
 * flow sampled curve). Originally ported from batches_dashboard.html drawOpt().
 *
 * Hover read-outs are DOM overlays (see OptimiserCard): every draw records its
 * geometry for the canvas, and optChartHit() maps a mouse x back to a sampled
 * point or bar, so the chart is never redrawn on hover.
 */

/** Optional context. The chart still draws without it (e.g. older saved scenarios). */
export interface OptChartOpts {
  /** Simulated throughput at the current flow (kg); otherwise read off the curve. */
  currentMassKg?: number;
  /** Crews in use: that bar is outlined in navy and tagged "Current". */
  currentCrews?: number;
  /** Recommended crews: that bar is tagged "Recommended". */
  recCrews?: number;
  /** Display units (default SI). Data and comparisons stay SI; only the
   *  axes and labels are drawn in the display unit. */
  units?: UnitSystem;
}

/** A target in tonnes as entered, without float noise: 20 → "20 t", 20.5 → "20.5 t" (US: "22.05 ton"). */
export const optGoalLabel = (t: number, sys: UnitSystem = "SI") => goalLabel(t, sys);

const isNum = (v: unknown): v is number => typeof v === "number" && isFinite(v);

// ── vs Flow summary (shared by the chart and the card's note) ────────────────

export interface FlowSweepStats {
  /** First point, scanning left to right, with the highest throughput. */
  peak: FlowSweepRow;
  /** What happens above the peak; "last" when the peak is the last point. */
  afterPeak: "falls" | "stays" | "last";
  /** Lowest point after the peak; null when the peak is the last point. */
  lowAfterPeak: FlowSweepRow | null;
  /** First point from the left that meets the goal; null with no goal or if none does. */
  firstAtGoal: FlowSweepRow | null;
  /** Flow between sampled points, kg/h (0 with a single sample). */
  step: number;
  minFlow: number;
  maxFlow: number;
  /** Throughput at the current flow, kg. */
  currentMassKg: number;
  /** Now and the peak are the same sampled point (within 0.5 % of flow). */
  nowIsPeak: boolean;
}

/** Linear read-off of the sampled curve at `flow`, clamped to the swept range. */
function massAtFlow(rows: FlowSweepRow[], flow: number): number {
  const last = rows[rows.length - 1];
  if (flow <= rows[0].m_flow_kg_h) return rows[0].mass_kg;
  for (let i = 1; i < rows.length; i++) {
    const a = rows[i - 1], b = rows[i];
    if (flow <= b.m_flow_kg_h) {
      const span = b.m_flow_kg_h - a.m_flow_kg_h;
      return span > 0 ? a.mass_kg + (b.mass_kg - a.mass_kg) * (flow - a.m_flow_kg_h) / span : b.mass_kg;
    }
  }
  return last.mass_kg;
}

/**
 * Key points of the flow sweep (rows ascending in flow, as the backend sends
 * them). Throughput is not always monotonic in flow, so the peak is the FIRST
 * highest point working left to right, and what follows it is reported too.
 */
export function flowSweepStats(
  flowSweep: FlowSweepRow[],
  goalT: number,
  currentFlow: number,
  currentMassKg?: number,
): FlowSweepStats | null {
  const rows = flowSweep;
  if (!rows.length) return null;

  // A later point must beat the peak outright (masses are whole batches, so
  // equal throughput is exactly equal) — ties keep the leftmost.
  let pi = 0;
  rows.forEach((r, i) => { if (r.mass_kg > rows[pi].mass_kg + 1e-6) pi = i; });
  const peak = rows[pi];

  const after = rows.slice(pi + 1);
  const lowAfterPeak = after.length
    ? after.reduce((lo, r) => (r.mass_kg < lo.mass_kg ? r : lo))
    : null;
  // "Falls" only when the drop shows at the 0.1 t the labels print.
  const afterPeak: FlowSweepStats["afterPeak"] = !lowAfterPeak ? "last"
    : Math.round(lowAfterPeak.mass_kg / 100) < Math.round(peak.mass_kg / 100) ? "falls" : "stays";

  const firstAtGoal = goalT > 0
    ? rows.find(r => r.mass_kg / 1000 >= goalT - 1e-9) ?? null
    : null;

  const nowIsPeak = currentFlow > 0
    && Math.abs(currentFlow - peak.m_flow_kg_h) <= 0.005 * Math.max(currentFlow, peak.m_flow_kg_h);

  return {
    peak, afterPeak, lowAfterPeak, firstAtGoal,
    step: rows.length > 1 ? rows[1].m_flow_kg_h - rows[0].m_flow_kg_h : 0,
    minFlow: rows[0].m_flow_kg_h,
    maxFlow: rows[rows.length - 1].m_flow_kg_h,
    currentMassKg: isNum(currentMassKg) ? currentMassKg : massAtFlow(rows, currentFlow),
    nowIsPeak,
  };
}

// ── Hover geometry (recorded per canvas on every draw) ───────────────────────

interface FlowGeom {
  mode: "flow";
  w: number; h: number; x0: number; x1: number; y0: number; y1: number;
  pts: { x: number; y: number; row: FlowSweepRow }[];
}
interface CrewGeom {
  mode: "crews";
  w: number; h: number; x0: number; x1: number; y0: number; y1: number;
  slotW: number; bw: number;
  bars: { x: number; y: number; row: CrewSweepRow }[];
}
const geoms = new WeakMap<HTMLCanvasElement, FlowGeom | CrewGeom>();

interface HoverBase {
  x: number;       // anchor: the sampled point / bar centre (CSS px from the canvas left)
  y: number;       // the point / bar top (CSS px from the canvas top)
  gap: number;     // clearance between the anchor and the tooltip
  top: number;     // plot top and bottom, for the crosshair
  bottom: number;
  width: number;   // canvas size at the last draw, to keep the tooltip inside it
  height: number;
}
/** What is under the mouse: the nearest sampled flow point, or the crew bar. */
export type OptHover =
  | (HoverBase & { mode: "flow"; row: FlowSweepRow })
  | (HoverBase & { mode: "crews"; row: CrewSweepRow });

/** Hit-test the last drawing on `canvas` at `mouseX` (CSS px from its left edge). */
export function optChartHit(canvas: HTMLCanvasElement, mouseX: number): OptHover | null {
  const g = geoms.get(canvas);
  if (!g || mouseX < g.x0 - 8 || mouseX > g.x1 + 8) return null;
  const base = { top: g.y1, bottom: g.y0, width: g.w, height: g.h };
  if (g.mode === "flow") {
    let best = g.pts[0];
    g.pts.forEach(p => { if (Math.abs(p.x - mouseX) < Math.abs(best.x - mouseX)) best = p; });
    return best ? { ...base, mode: "flow", x: best.x, y: best.y, gap: 10, row: best.row } : null;
  }
  // The whole column counts as the bar, not just its painted width.
  const i = Math.max(0, Math.min(g.bars.length - 1, Math.floor((mouseX - g.x0) / g.slotW)));
  const bar = g.bars[i];
  return bar ? { ...base, mode: "crews", x: bar.x, y: bar.y, gap: g.bw / 2 + 6, row: bar.row } : null;
}

// ── Drawing helpers ──────────────────────────────────────────────────────────

interface Plot {
  c: CanvasRenderingContext2D;
  W: number; H: number;
  x0: number; x1: number; y0: number; y1: number;
  sys: UnitSystem;
}

/** Throughput ticks in the display unit: max is in tonnes; ~8 % headroom. */
const yTicks = (maxT: number, sys: UnitSystem) => niceTicks(0, tToDisp(maxT, sys) * 1.08, 5);

const FONT = "Montserrat, Segoe UI, sans-serif";
const font = (px: number, weight = 400) => `${weight} ${px}px ${FONT}`;

/** A label's footprint, used to keep labels from overprinting each other. */
interface Box { l: number; r: number; t: number; b: number }
const overlaps = (a: Box, b: Box) => a.l < b.r && b.l < a.r && a.t < b.b && b.t < a.b;
/** Footprint of centred text whose baseline (bottom) is at `bottom`. */
const boxAbove = (x: number, w: number, bottom: number, h: number): Box =>
  ({ l: x - w / 2 - 2, r: x + w / 2 + 2, t: bottom - h, b: bottom });

/** Text with a white halo, so it stays legible where it crosses the curve or a marker line. */
function haloText(c: CanvasRenderingContext2D, text: string, x: number, y: number, color: string) {
  c.save();
  c.lineJoin = "round"; c.lineWidth = 3; c.strokeStyle = "rgba(255,255,255,0.9)";
  c.strokeText(text, x, y);
  c.fillStyle = color;
  c.fillText(text, x, y);
  c.restore();
}

/** Grid + tonne labels: round ticks from 0 with ~8 % headroom, all to the same decimals. */
function drawYAxis(p: Plot, maxT: number) {
  const { c, x0, x1, y0, y1, sys } = p;
  // Ticks are round numbers in the display unit; `sy` still takes tonnes.
  const yt = yTicks(maxT, sys);
  const dec = stepDecimals(yt.step);
  const syD = (d: number) => y0 - (d / yt.max) * (y0 - y1);
  const sy = (v: number) => syD(tToDisp(v, sys));
  c.font = font(11); c.lineWidth = 1;
  c.textAlign = "right"; c.textBaseline = "middle";
  yt.ticks.forEach(v => {
    c.globalAlpha = 0.5; c.strokeStyle = CHART.grid;
    c.beginPath(); c.moveTo(x0, syD(v)); c.lineTo(x1, syD(v)); c.stroke();
    c.globalAlpha = 1; c.fillStyle = CHART.axis;
    c.fillText(`${v.toFixed(dec)}${tickSuffix(sys)}`, x0 - 8, syD(v));
  });
  return sy;
}

/**
 * Left padding: 42 px as always in SI. US tick labels ("22.5 ton") are wider,
 * so the padding grows to fit the widest one.
 */
function leftPad(c: CanvasRenderingContext2D, maxT: number, sys: UnitSystem): number {
  if (sys !== "US") return 42;
  const yt = yTicks(maxT, sys);
  const dec = stepDecimals(yt.step);
  c.font = font(11);
  const w = Math.max(...yt.ticks.map(v => c.measureText(`${v.toFixed(dec)}${tickSuffix(sys)}`).width));
  return Math.max(42, Math.ceil(w) + 12);
}

/** Dashed target line + "Target 20 t" label, as before. Returns the label's footprint. */
function drawTarget(p: Plot, sy: (v: number) => number, goalT: number): Box {
  const { c, x0, x1 } = p;
  const y = sy(goalT);
  c.strokeStyle = CHART.goal; c.setLineDash([4, 3]); c.lineWidth = 1.2;
  c.beginPath(); c.moveTo(x0, y); c.lineTo(x1, y); c.stroke(); c.setLineDash([]);
  const text = `Target ${optGoalLabel(goalT, p.sys)}`;
  c.font = font(11); c.textAlign = "left"; c.textBaseline = "bottom";
  haloText(c, text, x0 + 4, y - 3, CHART.goal);   // halo: it can sit over the first crews bar
  return { l: x0 + 2, r: x0 + 6 + c.measureText(text).width, t: y - 16, b: y - 1 };
}

// ── Entry point ──────────────────────────────────────────────────────────────

export function drawOptChart(
  canvas: HTMLCanvasElement,
  crewSweep: CrewSweepRow[],
  flowSweep: FlowSweepRow[],
  mode: "crews" | "flow",
  goalT: number,
  currentFlow: number,
  opts: OptChartOpts = {},
) {
  geoms.delete(canvas);   // a hover must never read an earlier drawing
  const W = canvas.clientWidth;
  if (!W) return;
  const H = 260;
  const dpr = window.devicePixelRatio || 1;
  canvas.width  = W * dpr;
  canvas.height = H * dpr;
  const c = canvas.getContext("2d")!;
  c.setTransform(dpr, 0, 0, dpr, 0, 0);
  c.clearRect(0, 0, W, H);

  const sys: UnitSystem = opts.units ?? "SI";
  const goal = isNum(goalT) && goalT > 0 ? goalT : 0;
  // The same throughput maximum drawCrews / drawFlow scale to (tonnes).
  const sweepMaxT = mode === "crews"
    ? Math.max(...crewSweep.map(r => r.mass_kg / 1000), goal)
    : Math.max(...flowSweep.map(r => r.mass_kg / 1000), goal);
  // padR stays although the % axis is gone, so the plot lines up with the other charts.
  const padL = leftPad(c, sweepMaxT, sys), padR = 42, padT = 14, padB = 34;
  const p: Plot = { c, W, H, x0: padL, x1: W - padR, y0: H - padB, y1: padT, sys };

  if (mode === "crews" && crewSweep.length > 0) {
    geoms.set(canvas, drawCrews(p, crewSweep, goal, opts));
  } else if (mode === "flow") {
    const s = flowSweepStats(flowSweep, goal, currentFlow, opts.currentMassKg);
    if (s) geoms.set(canvas, drawFlow(p, flowSweep, s, goal, currentFlow));
  }
}

// ── vs Crews ─────────────────────────────────────────────────────────────────

function drawCrews(p: Plot, rows: CrewSweepRow[], goalT: number, opts: OptChartOpts): CrewGeom {
  const { c, W, H, x0, x1, y0, y1 } = p;
  const slotW = (x1 - x0) / rows.length;
  const sx = (i: number) => x0 + (i + 0.5) * slotW;
  const sy = drawYAxis(p, Math.max(...rows.map(r => r.mass_kg / 1000), goalT));
  const cur = isNum(opts.currentCrews) ? Math.round(opts.currentCrews) : null;
  const rec = isNum(opts.recCrews) ? Math.round(opts.recCrews) : null;
  const meets = (row: CrewSweepRow) => row.mass_kg / 1000 >= goalT - 1e-9;

  // Crew counts; the current one in bold navy, to match its outlined bar.
  c.textAlign = "center"; c.textBaseline = "top";
  rows.forEach((row, i) => {
    const isCur = row.n_crews === cur;
    c.font = font(11, isCur ? 700 : 400);
    c.fillStyle = isCur ? CHART.now : CHART.axis;
    c.fillText(String(row.n_crews), sx(i), y0 + 8);
  });

  // Bars (pale when short of the target); the current crew count outlined in navy.
  const bw = Math.max(3, slotW * 0.6);
  const bars = rows.map((row, i) => ({ row, x: sx(i), y: sy(row.mass_kg / 1000) }));
  bars.forEach(({ row, x, y }) => {
    c.fillStyle = meets(row) ? CHART.flow : CHART.barDim;
    c.fillRect(x - bw / 2, y, bw, y0 - y);
    if (row.n_crews !== cur) return;
    if (y0 - y >= 3) {
      c.strokeStyle = CHART.now; c.lineWidth = 2;
      c.strokeRect(x - bw / 2 + 1, y + 1, bw - 2, y0 - y - 2);   // inside the bar
    } else {
      c.fillStyle = CHART.now;
      c.fillRect(x - bw / 2, y0 - 2, bw, 2);                     // no bar to outline
    }
  });

  // Target line over the bars; the labels go on top of it.
  const placed: Box[] = goalT > 0 ? [drawTarget(p, sy, goalT)] : [];
  const clear = (b: Box) => placed.every(q => !overlaps(q, b));

  // Tonnes above each bar; utilisation inside the bar's top when it fits, else
  // in grey just above the tonnes. Returns where the next label up would sit.
  const stackTop = bars.map(({ row, x, y }) => {
    const util = `${Math.round(row.crew_util * 100)}%`;
    c.font = font(10, 600);
    const inside = y0 - y >= 18 && c.measureText(util).width + 6 <= bw;
    c.textAlign = "center";
    if (inside) {
      // White reads on the full-colour bars; the pale bars need dark ink.
      c.fillStyle = meets(row) ? "#fff" : CHART.ink;
      c.textBaseline = "top";
      c.fillText(util, x, y + 4);
    }
    c.textBaseline = "bottom";
    let yb = y - 3;
    const val = tToDisp(row.mass_kg / 1000, p.sys).toFixed(1);
    c.font = font(11); c.fillStyle = CHART.ink;
    c.fillText(val, x, yb);
    placed.push(boxAbove(x, c.measureText(val).width, yb, 12));
    yb -= 13;
    if (!inside) {
      c.font = font(10); c.fillStyle = CHART.axis;
      c.fillText(util, x, yb);
      placed.push(boxAbove(x, c.measureText(util).width, yb, 11));
      yb -= 12;
    }
    return yb;
  });

  // Tags above the value labels, lifted a line while they would overprint another label.
  // "Current" is navy like its outline and legend swatch; "recommended" is green.
  c.font = font(10, 600);
  c.textAlign = "left"; c.textBaseline = "bottom";
  bars.forEach(({ row, x }, i) => {
    const isCur = row.n_crews === cur, isRec = row.n_crews === rec;
    const parts: [string, string][] = [];
    if (isCur) parts.push([isRec ? "Current · " : "Current", CHART.now]);
    if (isRec) parts.push([isCur ? "recommended" : "Recommended", CHART.peak]);
    if (!parts.length) return;
    const w = parts.reduce((sum, [t]) => sum + c.measureText(t).width, 0);
    const l = Math.max(2, Math.min(x - w / 2, W - 2 - w));
    const boxAt = (bottom: number): Box => ({ l: l - 2, r: l + w + 2, t: bottom - 11, b: bottom });
    let yb = stackTop[i];
    while (yb > 13 && !clear(boxAt(yb))) yb -= 12;
    placed.push(boxAt(yb));
    let tx = l;
    parts.forEach(([t, col]) => { c.fillStyle = col; c.fillText(t, tx, yb); tx += c.measureText(t).width; });
  });

  c.font = font(11); c.fillStyle = CHART.axis; c.textAlign = "center"; c.textBaseline = "top";
  c.fillText("Crews", (x0 + x1) / 2, H - 12);

  return { mode: "crews", w: W, h: H, x0, x1, y0, y1, slotW, bw, bars };
}

// ── vs Flow ──────────────────────────────────────────────────────────────────

function drawFlow(
  p: Plot, rows: FlowSweepRow[], s: FlowSweepStats, goalT: number, currentFlow: number,
): FlowGeom {
  const { c, W, H, x0, x1, y0, y1, sys } = p;
  const fmtT = (kg: number) => fmtMassT(kg, sys);
  const kgh = (f: number) => fmtFlow(f, sys);

  // X: round ticks from just below the lowest swept flow, not from 0. The
  // ticks are round numbers in the display unit (kg/h or lb/hr); `sx` takes kg/h.
  let lo = flowToDisp(s.minFlow, sys), hi = flowToDisp(s.maxFlow, sys);
  if (!(hi - lo > 1)) { lo = Math.max(0, lo * 0.8); hi = Math.max(hi * 1.2, lo + 100); }  // one sample
  const xt = niceTicks(lo, hi, 6);
  const sxD = (d: number) => x0 + ((d - xt.min) / (xt.max - xt.min)) * (x1 - x0);
  const sx = (f: number) => sxD(flowToDisp(f, sys));

  const peakOn = s.peak.mass_kg > 0;   // an all-zero sweep has no meaningful peak
  const px = sx(s.peak.m_flow_kg_h);

  // Above the peak extra flow adds nothing: shade it lightly (not when the peak is the last point).
  if (peakOn && s.afterPeak !== "last") {
    c.globalAlpha = 0.06; c.fillStyle = CHART.peak;
    c.fillRect(px, y1, x1 - px, y0 - y1);
    c.globalAlpha = 1;
  }

  const sy = drawYAxis(p, Math.max(...rows.map(r => r.mass_kg / 1000), goalT));

  // Flow labels with thousands separators, thinned if they would collide on a narrow chart.
  c.font = font(11); c.fillStyle = CHART.axis; c.textAlign = "center"; c.textBaseline = "top";
  const tickW = Math.max(...xt.ticks.map(v => c.measureText(fmtInt(v)).width));
  const every = Math.max(1, Math.ceil((tickW + 10) * (xt.ticks.length - 1) / Math.max(1, x1 - x0)));
  xt.ticks.forEach((v, i) => { if (i % every === 0) c.fillText(fmtInt(v), sxD(v), y0 + 8); });

  const placed: Box[] = goalT > 0 ? [drawTarget(p, sy, goalT)] : [];
  const clear = (b: Box) => placed.every(q => !overlaps(q, b));

  // Now / peak: dashed verticals — one two-tone line when they are the same point.
  const curD = flowToDisp(currentFlow, sys);
  const nowOn = currentFlow > 0 && curD >= xt.min && curD <= xt.max;
  const merged = nowOn && peakOn && s.nowIsPeak;
  const nx = sx(currentFlow);
  const vline = (x: number, color: string, dashOffset = 0) => {
    c.strokeStyle = color; c.lineWidth = 1.2;
    c.setLineDash([4, 4]); c.lineDashOffset = dashOffset;
    c.beginPath(); c.moveTo(x, y1); c.lineTo(x, y0); c.stroke();
    c.setLineDash([]); c.lineDashOffset = 0;
  };
  if (merged) {
    vline(px, CHART.peak);
    vline(px, CHART.now, 4);   // fills the gaps: alternating navy / green
  } else {
    if (peakOn) vline(px, CHART.peak);
    if (nowOn) vline(nx, CHART.now);
  }

  // Curve, with a dot at every sampled point: it is sampled, not continuous.
  const pts = rows.map(row => ({ x: sx(row.m_flow_kg_h), y: sy(row.mass_kg / 1000), row }));
  c.strokeStyle = CHART.flow; c.lineWidth = 2; c.lineJoin = "round";
  c.beginPath();
  pts.forEach((pt, i) => { i === 0 ? c.moveTo(pt.x, pt.y) : c.lineTo(pt.x, pt.y); });
  c.stroke();
  c.lineJoin = "miter";
  c.fillStyle = CHART.flow;
  pts.forEach(pt => { c.beginPath(); c.arc(pt.x, pt.y, 2.5, 0, Math.PI * 2); c.fill(); });

  // Marker dots on the curve.
  const dot = (x: number, y: number, fill: string, ring = "#fff", ringW = 1.5) => {
    c.beginPath(); c.arc(x, y, 4.5, 0, Math.PI * 2);
    c.fillStyle = fill; c.fill();
    c.lineWidth = ringW; c.strokeStyle = ring; c.stroke();
  };
  const py = sy(s.peak.mass_kg / 1000);
  if (merged) {
    dot(px, py, CHART.peak, CHART.now, 2);
  } else {
    if (peakOn) dot(px, py, CHART.peak);
    if (nowOn) dot(nx, sy(massAtFlow(rows, currentFlow) / 1000), CHART.now);
  }
  // First point that meets the target: a red ring, so the dot inside stays visible.
  const g = s.firstAtGoal;
  const gx = g ? sx(g.m_flow_kg_h) : 0;
  if (g) {
    c.beginPath(); c.arc(gx, sy(g.mass_kg / 1000), 7, 0, Math.PI * 2);
    c.lineWidth = 1.8; c.strokeStyle = CHART.goal; c.stroke();
  }

  // Labels at the top of the plot beside their line: to the right, flipped to
  // the left near the right edge, and a line lower while they would overlap.
  const topLabel = (text: string, x: number, color: string) => {
    c.font = font(11, 600);
    const w = c.measureText(text).width;
    const side = x + 5 + w <= x1 - 2 ? x + 5 : x - 5 - w;
    const l = Math.max(x0 + 2, Math.min(side, x1 - 2 - w));
    const boxAt = (line: number): Box => ({ l: l - 2, r: l + w + 2, t: y1 + 2 + line * 13, b: y1 + 15 + line * 13 });
    let line = 0;
    while (line < 3 && !clear(boxAt(line))) line++;
    placed.push(boxAt(line));
    c.textAlign = "left"; c.textBaseline = "top";
    haloText(c, text, l, y1 + 3 + line * 13, color);
  };
  const nowText = `${kgh(currentFlow)} · ${fmtT(s.currentMassKg)}`;
  if (merged) {
    topLabel(`Now = peak · ${nowText}`, px, CHART.now);
  } else {
    if (nowOn) topLabel(`Now ${nowText}`, nx, CHART.now);
    if (peakOn) topLabel(`Peak ${fmtT(s.peak.mass_kg)} at ${kgh(s.peak.m_flow_kg_h)}`, px, CHART.peak);
  }

  // "20 t from 1,600 kg/h": just below the target line beside the ring, clear
  // of the other labels; above the line only if there is no room below.
  if (g) {
    const text = `${optGoalLabel(goalT, sys)} from ${kgh(g.m_flow_kg_h)}`;
    c.font = font(10, 600);
    const w = c.measureText(text).width;
    const yl = sy(goalT);
    const boxAt = (l: number, t: number): Box => ({ l: l - 2, r: l + w + 2, t, b: t + 13 });
    const options = [
      boxAt(gx + 10, yl + 3), boxAt(gx - 10 - w, yl + 3),
      boxAt(gx + 10, yl - 16), boxAt(gx - 10 - w, yl - 16),
    ];
    const inPlot = (b: Box) => b.l >= x0 && b.r <= x1 && b.t >= y1 && b.b <= y0;
    const pick = options.find(b => inPlot(b) && clear(b)) ?? options.find(inPlot) ?? options[0];
    placed.push(pick);
    c.textAlign = "left"; c.textBaseline = "top";
    haloText(c, text, pick.l + 2, pick.t + 1, CHART.goal);
  }

  c.font = font(11); c.fillStyle = CHART.axis; c.textAlign = "center"; c.textBaseline = "top";
  c.fillText(`Raw flow (${flowUnit(sys)})`, (x0 + x1) / 2, H - 12);

  return { mode: "flow", w: W, h: H, x0, x1, y0, y1, pts };
}
